<template>
  <model-form
    ref="modelForm"
    :model="paymentConnectorConfiguration"
    :submodel="forStation"
    modal-type="modal"
    width="600px"
    @on-show="setupConfigurationOptions"
  >
    <q-tabs v-model="tab">
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        name="configuration"
        label="configuration"
      />
      <q-tab
        name="logging"
        label="Logging"
      />
      <q-tab
        v-if="requiresTransactionNumber"
        name="transaction-numbers"
        label="Transaction Numbers"
      />
    </q-tabs>
    <q-tab-panels
      v-model="tab"
      animated
    >
      <q-tab-panel
        name="details"
        class="q-gutter-sm"
      >
        <div class="row q-gutter-md items-start">
          <q-select
            v-if="forStation && paymentConnectorConfiguration.isLocal"
            v-model="paymentConnectorConfiguration.name"
            class="col"
            option-label="label"
            map-options
            option-value="value"
            :options="connectorTypeOptions"
            :rules="[val => !!val && val !== null || 'Field is required']"
            label="Connector Name"
            emit-value
          />
          <q-input
            v-else
            :model-value="$_.startCase(paymentConnectorConfiguration.name)"
            class="col"
            label="Connector Name"
            readonly
          />
        </div>
        <div class="row q-gutter-md q-mt-sm items-start">
          <q-checkbox
            v-if="!paymentConnectorConfiguration.registerStation && !forStation"
            v-model="paymentConnectorConfiguration.enabled"
            class="col-auto"
            label="Enabled"
          />
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="configuration"
        class="q-gutter-sm"
      >
        <div
          v-if="connectorOptions.length"
          class="q-gutter-md items-start q-mt-md"
        >
          <div
            v-for="option in connectorOptions"
            :key="option"
          >
            <q-select
              v-if="computedConnectorOptions.includes(option)"
              v-model="configuration[option]"
              :label="$_.startCase(option)"
              :options="paymentConnectorConfiguration.computedSiteConfigurationOptions[option]"
              :multiple="Array.isArray(paymentConnectorConfiguration.computedSiteConfigurationOptions[option])"
              :use-chips="Array.isArray(paymentConnectorConfiguration.computedSiteConfigurationOptions[option])"
              map-options
              option-label="label"
              option-value="value"
              emit-value
            />
            <q-select
              v-else-if="option === 'default_stripe_reader_serial_number'"
              v-model="configuration[option]"
              label="Default Stripe Reader"
              :options="availableStripeReaders"
              map-options
              option-label="label"
              option-value="value"
              emit-value
            />
            <q-checkbox
              v-else-if="typeof configuration[option] === 'boolean'"
              v-model="configuration[option]"
              class="col-6"
              :label="$_.startCase(option)"
            />
            <q-input
              v-else
              v-model="configuration[option]"
              class="col-6"
              :label="$_.startCase(option)"
              :rules="[
                () => !!configuration[option] || 'Field is required'
              ]"
            />
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="logging"
        class="q-gutter-sm"
      >
        <div
          v-if="currentUser.superUser"
          class="text-caption"
        >
          This controls logging for this payment connector configuration.<br>
          Any changes made at this level will propagate to children.<br>
          E.g. If you enable logging for {{ paymentConnectorConfiguration.name }} global configuration, all site {{ paymentConnectorConfiguration.name }} configurations will have logging enabled<br>
          all site registers {{ paymentConnectorConfiguration.name }} configurations will also be enabled.<br>
        </div>

        <div
          v-if="currentUser.superUser"
          class="row q-gutter-md items-start"
        >
          <q-checkbox
            v-model="paymentConnectorConfiguration.loggingEnabled"
            class="col-auto"
            label="Logging Enabled"
          />
        </div>

        <payment-connector-log-table
          v-if="!paymentConnectorConfiguration.isLocal"
          :payment-connector-configuration-id="paymentConnectorConfiguration.id"
        />
      </q-tab-panel>
      <q-tab-panel
        name="transaction-numbers"
        class="q-gutter-sm"
      >
        <payment-connector-transaction-number-table
          :payment-connector-configuration-id="paymentConnectorConfiguration.id"
        />
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>

import RegisterStation from 'src/models/register/RegisterStation'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import PaymentConnectorLogTable from 'src/modules/admin/payment-connector/PaymentConnectorLogTable'
import PaymentConnectorTransactionNumberTable from 'src/modules/admin/payment-connector/PaymentConnectorTransactionNumberTable'

export default {
  name: 'PaymentConnectorConfigurationForm',
  components: {
    ModelForm,
    PaymentConnectorLogTable,
    PaymentConnectorTransactionNumberTable
  },
  props: {
    registerStation: {
      type: RegisterStation,
      required: false,
      default: null
    },
    canEnable: {
      type: Boolean,
      default: true
    },
    existingConnectors: {
      type: Array,
      default: () => []
    }
  },
  models: {
    paymentConnectorConfiguration: {
      loggingEnabled: true,
      configurationIgnoreKeys: true,
      computedSiteConfigurationOptions: true
    },
    currentSite: {
      paymentConnectorTerminalOptions: true,
      stripeTerminalLocationIds: true,
      immediate: true
    }
  },
  context: ['currentUser'],
  data () {
    return {
      availableStripeReaders: [],
      configuration: {},
      tab: 'details'
    }
  },
  computed: {
    forStation () {
      return !!this.registerStation
    },
    connectorOptions () {
      const allKeys = _.keys(this.configuration)
      // Some options we want to ignore as they are defined by the payment service implementation
      const ignoreKeys = this.paymentConnectorConfiguration.configurationIgnoreKeys || []
      return _.difference(allKeys, ignoreKeys)
    },
    computedConnectorOptions () {
      return _.keys(this.paymentConnectorConfiguration.computedSiteConfigurationOptions || {})
    },
    connectorTypeOptions () {
      return _(this.currentSite.paymentConnectorTerminalOptions).map(option => {
        if (this.existingConnectors.includes(option.name)) {
          return null
        }
        return {
          label: _.startCase(option.name),
          value: option.name
        }
      }).compact().value()
    },
    configurationValues () {
      return _.values(this.configuration)
    },
    requiresTransactionNumber () {
      return this.paymentConnectorConfiguration?.originalData?.configuration?.require_transaction_number
    }
  },
  watch: {
    'paymentConnectorConfiguration.name' () {
      this.setupConfigurationOptions()
    },
    configurationValues () {
      this.paymentConnectorConfiguration.configuration = this.configuration
    }
  },
  async mounted () {
    await this.currentSite.ensureLoaded()

    if (this.forStation && this.registerStation?.registerType?.stripeTerminalLocationId) {
      const result = await this.$payments.discoverTerminalReaders(this.registerStation?.registerType?.stripeTerminalLocationId)
      if (!result?.readers) {
        return
      }
      _.each(result.readers, reader => this.availableStripeReaders.push({
        label: reader.label,
        value: reader.serial_number
      }))
    }
  },
  methods: {
    async setupConfigurationOptions () {
      await this.currentSite.ensureLoaded()
      if (this.paymentConnectorConfiguration.isLocal && this.paymentConnectorConfiguration.name) {
        // Creating a new register station configuration with a connector selected
        // This means we need to load the configuration options for the selected connector
        const foundConfigurationJson = _.find(this.currentSite.paymentConnectorTerminalOptions, option => option.name === this.paymentConnectorConfiguration.name)?.configuration
        this.configuration = foundConfigurationJson ? JSON.parse(foundConfigurationJson) : {}
      } else if (this.paymentConnectorConfiguration.isLocal) {
        // Creating a new register station configuration with no connector selected
        // We need to clear the configuration options
        this.configuration = {}
      } else if (this.paymentConnectorConfiguration.isRemote && this.forStation) {
        // Editing a register station configuration
        // We need to merge the configuration options from the defaults incase new options have been added
        const foundConfigurationJson = _.find(this.currentSite.paymentConnectorTerminalOptions, option => option.name === this.paymentConnectorConfiguration.name)?.configuration
        const newConfiguration = foundConfigurationJson ? JSON.parse(foundConfigurationJson) : {}
        _(newConfiguration).keys().each(key => {
          newConfiguration[key] = this.paymentConnectorConfiguration.configuration[key]
        })
        this.configuration = newConfiguration
      } else {
        // Editing a global or site configuration
        // Site and global configurations are created by the API and will always have a configuration
        this.configuration = this.paymentConnectorConfiguration.configuration
      }
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
